<template>
  <div class="bg-white rounded-lg shadow p-4">
    <div class="flex justify-between items-start">
      <div class="flex items-start">
        <div v-if="userProfile && userProfile.avatar_url" class="h-10 w-10 rounded-full overflow-hidden mr-3">
          <img :src="userProfile.avatar_url" alt="User avatar" class="h-full w-full object-cover" />
        </div>
        <div v-else class="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden mr-3">
          <span class="text-sm font-semibold text-primary">
            {{ userInitials }}
          </span>
        </div>
        <div>
          <div class="flex items-center">
            <span class="font-medium">{{ userProfile ? userProfile.name : userName }}</span>
            <span class="text-xs text-gray-500 ml-2">{{ formattedDate }}</span>
          </div>
          <p v-if="!isEditing" class="mt-1 text-gray-700">{{ comment.content }}</p>
          
          <!-- Edit form -->
          <div v-if="isEditing" class="mt-1">
            <textarea
              v-model="editContent"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows="3"
            ></textarea>
            <div class="flex justify-end mt-2 space-x-2">
              <button
                @click="cancelEdit"
                class="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="saveEdit"
                class="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                :disabled="isLoading"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Actions dropdown for comment owner -->
      <div v-if="isOwner" class="relative">
        <button 
          @click="toggleDropdown"
          class="text-gray-400 hover:text-gray-600"
        >
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
          </svg>
        </button>
        
        <!-- Dropdown menu -->
        <div 
          v-if="showDropdown"
          class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10"
        >
          <div class="py-1">
            <button 
              @click="startEdit"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Edit
            </button>
            <button 
              @click="confirmDelete"
              class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, onMounted } from 'vue';
import { IComment } from '@/types/comment';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import type { Profile } from '@/types/supabase';

export default defineComponent({
  name: 'CommentItem',
  props: {
    comment: {
      type: Object as PropType<IComment>,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['edit', 'delete'],
  setup(props, { emit }) {
    const { user } = useAuth();
    const showDropdown = ref(false);
    const isEditing = ref(false);
    const editContent = ref(props.comment.content);
    const userProfile = ref<Profile | null>(null);
    const profileLoading = ref(false);
    
    // Check if current user is the comment owner
    const isOwner = computed(() => {
      return user.value?.id === props.comment.created_by;
    });
    
    // Fetch user profile information
    async function fetchUserProfile() {
      profileLoading.value = true;
      
      try {
        logger.info('Fetching user profile for comment', LogCategory.DATABASE, {
          userId: props.comment.created_by
        });
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', props.comment.created_by)
          .single();
        
        if (error) {
          logger.error('Error fetching user profile', LogCategory.DATABASE, { error });
          return;
        }
        
        if (data) {
          userProfile.value = data;
          logger.info('User profile fetched successfully', LogCategory.DATABASE, {
            userName: data.name,
            userRole: data.business_role
          });
        }
      } catch (err) {
        logger.error('Error in fetchUserProfile', LogCategory.DATABASE, { error: err });
      } finally {
        profileLoading.value = false;
      }
    }
    
    // Fetch user profile on component mount
    onMounted(() => {
      fetchUserProfile();
    });
    
    // Format the comment date
    const formattedDate = computed(() => {
      const date = new Date(props.comment.created_at);
      
      // If it's today, show time only
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // Otherwise show date and time
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    });
    
    // Get user initials from user ID
    const userInitials = computed(() => {
      // In a real implementation, you would fetch the user's name from a user service
      // For now, just return the first two characters of the user ID
      return props.comment.created_by.substring(0, 2).toUpperCase();
    });
    
    // Get user name from user ID
    const userName = computed(() => {
      // In a real implementation, you would fetch the user's name from a user service
      // For now, just return "User" + first 4 characters of the user ID
      return `User ${props.comment.created_by.substring(0, 4)}`;
    });
    
    // Toggle dropdown menu
    function toggleDropdown() {
      showDropdown.value = !showDropdown.value;
    }
    
    // Start editing the comment
    function startEdit() {
      isEditing.value = true;
      editContent.value = props.comment.content;
      showDropdown.value = false;
    }
    
    // Cancel editing
    function cancelEdit() {
      isEditing.value = false;
      editContent.value = props.comment.content;
    }
    
    // Save edited comment
    function saveEdit() {
      if (editContent.value.trim() === props.comment.content) {
        isEditing.value = false;
        return;
      }
      
      emit('edit', {
        id: props.comment.id,
        content: editContent.value.trim()
      });
      
      isEditing.value = false;
    }
    
    // Confirm delete
    function confirmDelete() {
      emit('delete', props.comment.id);
      showDropdown.value = false;
    }
    
    return {
      isOwner,
      formattedDate,
      userInitials,
      userName,
      userProfile,
      showDropdown,
      isEditing,
      editContent,
      profileLoading,
      toggleDropdown,
      startEdit,
      cancelEdit,
      saveEdit,
      confirmDelete
    };
  }
});
</script>